import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { supabase } from '@/lib/supabase';
import { mockShopProducts } from '@/mocks/shop';
import { mapDbProduct, type ShopProductDetail } from '@/lib/shop';
import { useCart } from '@/context/CartContext';

function formatPrice(price: number) {
  return price.toLocaleString('fr-FR');
}

function phoneDigits(phone: string) {
  return phone.replace(/\D/g, '');
}

function ShopProductDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ShopProductDetail | null>(null);
  const [related, setRelated] = useState<ShopProductDetail[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(0);
  const { addItem, openCart } = useCart();

  useEffect(() => {
    if (!id) return;
    setActiveImage(0);

    const mock = mockShopProducts.find((p) => p.id === id);
    if (mock) {
      setProduct({ ...mock, images: mock.image ? [mock.image] : [], files: [] });
      setRelated(mockShopProducts.filter((p) => p.id !== id).slice(0, 4));
      setLoading(false);
      return;
    }

    let mounted = true;
    setLoading(true);

    (async () => {
      try {
        const { data: catData } = await supabase.from('categories').select('id, name');
        const catMap: Record<string, string> = {};
        (catData || []).forEach((c: { id: string; name: string }) => {
          catMap[c.id] = c.name;
        });

        const { data, error } = await supabase
          .from('listings')
          .select('*')
          .eq('id', id)
          .maybeSingle();
        if (error) throw error;
        if (mounted && data) {
          const catName = data.category_id ? catMap[data.category_id] || 'Boutique' : 'Boutique';
          setProduct(mapDbProduct(data, catName));
        }

        const { data: relData } = await supabase
          .from('listings')
          .select('*')
          .eq('type', 'produit')
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(8);
        if (mounted && relData) {
          const mapped = relData
            .filter((r) => r.id !== id)
            .slice(0, 4)
            .map((r) =>
              mapDbProduct(r, r.category_id ? catMap[r.category_id] || 'Boutique' : 'Boutique'),
            );
          setRelated(mapped);
        }
      } catch {
        if (mounted) setProduct(null);
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
          <i className="ri-loader-4-line text-2xl text-primary-400 animate-spin" />
        </div>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen bg-background-50 flex flex-col items-center justify-center px-4">
        <div className="w-20 h-20 flex items-center justify-center rounded-full bg-background-100 mb-6">
          <i className="ri-shopping-bag-3-line text-3xl text-foreground-400" />
        </div>
        <h1 className="font-heading font-bold text-2xl text-foreground-50 mb-2">Produit introuvable</h1>
        <p className="text-foreground-400 mb-6 text-center">Ce produit n'existe pas ou a été retiré.</p>
        <button
          onClick={() => navigate('/shop')}
          className="px-6 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors whitespace-nowrap"
        >
          Retour à la Boutique
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <main className="pt-20 md:pt-24">
        {/* Breadcrumb */}
        <div className="w-full px-4 md:px-8 lg:px-12 py-4">
          <nav className="flex items-center gap-2 text-sm text-foreground-400">
            <Link to="/" className="hover:text-primary-400 transition-colors">Accueil</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/shop" className="hover:text-primary-400 transition-colors">Shop</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-400 truncate">{product.title}</span>
          </nav>
        </div>

        {/* Gallery */}
        <section className="w-full px-4 md:px-8 lg:px-12 mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 relative overflow-hidden rounded-xl h-72 md:h-[420px] bg-background-100">
              {product.images.length > 0 ? (
                <img
                  src={product.images[activeImage]}
                  alt={product.title}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <i className="ri-image-line text-5xl text-foreground-400" />
                </div>
              )}
            </div>
            {product.images.length > 1 && (
              <div className="grid grid-cols-3 lg:grid-cols-1 gap-3">
                {product.images.slice(0, 3).map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setActiveImage(idx)}
                    className={`relative overflow-hidden rounded-xl h-20 md:h-[132px] border-2 transition-colors ${
                      activeImage === idx ? 'border-primary-400' : 'border-transparent'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Content */}
        <section className="w-full px-4 md:px-8 lg:px-12 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Main info */}
            <div className="lg:col-span-2 space-y-8">
              <article data-product-shop>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div>
                    <span className="inline-block px-3 py-1 rounded-full text-xs font-semibold bg-accent-500 text-foreground-950 mb-3">
                      {product.category}
                    </span>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl text-foreground-50 mb-2">
                      {product.title}
                    </h1>
                    <p className="flex items-center gap-2 text-foreground-400">
                      <span className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-map-pin-2-line text-primary-400" />
                      </span>
                      {product.city}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <span className="font-heading font-bold text-2xl md:text-3xl text-primary-400 whitespace-nowrap">
                      {formatPrice(product.price)} FCFA
                    </span>
                  </div>
                </div>
              </article>

              {/* Description */}
              <div>
                <h2 className="font-heading font-bold text-xl text-foreground-50 mb-3">Description</h2>
                <p className="text-foreground-500 leading-relaxed whitespace-pre-line">
                  {product.description}
                </p>
              </div>

              {/* Files */}
              {product.files.length > 0 && (
                <div>
                  <h2 className="font-heading font-bold text-xl text-foreground-50 mb-3">
                    Fichiers joints
                  </h2>
                  <div className="space-y-2">
                    {product.files.map((file, idx) => (
                      <a
                        key={idx}
                        href={file}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-3 p-3 rounded-lg bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-colors"
                      >
                        <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-secondary-500/10 shrink-0">
                          <i className="ri-file-3-line text-secondary-400" />
                        </span>
                        <span className="flex-1 text-sm text-foreground-50 truncate">Document {idx + 1}</span>
                        <i className="ri-download-2-line text-foreground-400" />
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              <div className="bg-background-100 border border-background-200/70 rounded-xl p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-500/10">
                    <i className="ri-store-2-line text-xl text-primary-400" />
                  </div>
                  <div>
                    <span className="block font-semibold text-foreground-50">{product.sellerName}</span>
                    <span className="block text-xs text-foreground-400">Vendeur WorldApeiron</span>
                  </div>
                </div>

                <button
                  onClick={() => {
                    addItem({
                      id: product.id,
                      title: product.title,
                      price: product.price,
                      image: product.image,
                      sellerName: product.sellerName,
                    });
                    openCart();
                  }}
                  className="flex items-center justify-center gap-2 w-full px-4 py-3 bg-primary-500 text-foreground-950 font-semibold rounded-lg hover:bg-primary-400 transition-colors mb-3 whitespace-nowrap"
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className="ri-shopping-cart-2-line" />
                  </span>
                  Ajouter au panier
                </button>
                {product.sellerPhone && (
                  <>
                    <a
                      href={`tel:${product.sellerPhone.replace(/\s/g, '')}`}
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-primary-400/40 text-primary-400 font-semibold rounded-lg hover:bg-primary-500/10 transition-colors mb-3 whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-phone-line" />
                      </span>
                      Appeler
                    </a>
                    <a
                      href={`https://wa.me/${phoneDigits(product.sellerPhone)}`}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center justify-center gap-2 w-full px-4 py-3 border border-secondary-400/40 text-secondary-300 font-semibold rounded-lg hover:bg-secondary-500/10 transition-colors mb-3 whitespace-nowrap"
                    >
                      <span className="w-5 h-5 flex items-center justify-center">
                        <i className="ri-whatsapp-line" />
                      </span>
                      WhatsApp
                    </a>
                  </>
                )}

                <p className="text-xs text-foreground-400 text-center">
                  Publié le {product.postedAt}
                </p>
              </div>
            </div>
          </div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-16">
              <h2 className="font-heading font-bold text-xl md:text-2xl text-foreground-50 mb-6">
                Produits similaires
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
                {related.map((rel) => (
                  <Link
                    key={rel.id}
                    to={`/shop/${rel.id}`}
                    className="group rounded-xl overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all duration-300"
                  >
                    <div className="h-40 w-full overflow-hidden bg-background-200/60">
                      {rel.image ? (
                        <img
                          src={rel.image}
                          alt={rel.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <i className="ri-image-line text-3xl text-foreground-400" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-heading font-semibold text-sm text-foreground-50 mb-1 line-clamp-1">
                        {rel.title}
                      </h3>
                      <span className="font-heading font-bold text-primary-400">
                        {formatPrice(rel.price)} FCFA
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </div>
  );
}

export default ShopProductDetailPage;